import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import {
  FormGroup,
  Button,
  Intent,
} from "@blueprintjs/core";
import FormDrawer from "components/FormDrawer";
import ClientAutocomplete from "components/ClientAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import Toast from "helpers/toaster";
import { observable } from 'mobx';
import { FormSection, FieldError } from "components/FormBits";

const CLIENT_ID = "change-client";
const LOCATION_ID = "change-client-location";

const focusField = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.focus();
    element.scrollIntoView({ block: "center" });
  }
};

@inject("viewStore", "quoteStore")
@observer
class UpdateClientModal extends Component {
  @observable selectedClient = null;
  @observable selectedLocation = null;
  @observable errors = {};

  componentDidMount() {
    const {
      quoteStore: {
        quote: { 
          object 
        }
      }
    } = this.props;
    this.selectedClient = object.attributes.client && {attributes: object.attributes.client};
    this.selectedLocation = object.attributes.current_location && {attributes: { ...object.attributes.current_location, name: object.attributes.current_location.full_address}};
  }

  updateClient() {
    let {
      props: {
        quoteStore: {
          quote: { 
            object 
          }
        }
      }
    } = this;

    if(this.selectedClient === null) {
      this.errors = { client: "Pick the client this estimate is for." };
      focusField(CLIENT_ID);
      return;
    }
    if(this.selectedLocation === null) {
      this.errors = { location: "Pick one of this client's locations." };
      focusField(LOCATION_ID);
      return;
    }
    this.errors = {};

    object.attributes.client = this.selectedClient;
    object.attributes.client_id = this.selectedClient.id;
    object.attributes.current_location = this.selectedLocation;
    object.attributes.location_id = this.selectedLocation.id;

    this.props.quoteStore
      .update()
      .then(response => {
        this.props.onClose();
        Toast.show({
          icon: "thumbs-up",
          message: "Client updated successfully!",
          intent: Intent.SUCCESS
        });
      })
      .catch(error => {
        Toast.show({
          icon: "warning-sign",
          message: "Unable to update client!",
          intent: Intent.DANGER
        });
      });
  }

  handleCloseDailog() {
    this.errors = {};
    this.props.onClose();
  }

  onClientClear = () => {
    this.selectedClient = null;
    this.selectedLocation = null;
  };

  onLocationClear = () => {
    this.selectedLocation = null;
  };

  render() {
    let { object } = this.props.quoteStore.quote;
    const busy = Boolean(this.props.quoteStore.quote.isLoading);
    const close = this.handleCloseDailog.bind(this);
    const errors = this.errors;
    // Search the chosen client's locations (it starts as the estimate's own client).
    const selected = this.selectedClient;
    const locationScope =
      (selected && (selected.id || (selected.attributes && selected.attributes.id))) ||
      (object && object.attributes && object.attributes.client && object.attributes.client.id);
    return (
      <FormDrawer
        isOpen={this.props.isOpen}
        title="Change client"
        icon="person"
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon="floppy-disk"
              loading={busy}
              onClick={this.updateClient.bind(this)}
              text="Save client"
            />
          </>
        }
      >
        <FormSection title="Client and location" hint="Who this estimate is for and where the work is.">
          <FormGroup
            label="Client"
            labelFor={CLIENT_ID}
            labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
            helperText={this.selectedClient ? "To pick someone else, clear this client first." : "Search by name, phone number or email."}
            className={errors.client ? "has-error" : undefined}
          >
            <ClientAutocomplete
              inputProps={{
                id: CLIENT_ID,
                placeholder: "e.g. Jane Cooper or 555-123-4567",
                "aria-required": true,
                "aria-invalid": errors.client ? true : undefined,
                "aria-describedby": errors.client ? `${CLIENT_ID}-error` : undefined
              }}
              value={this.selectedClient}
              onClientSelect={client => {
                if (client) {
                  this.selectedClient = client;
                  this.errors = {};
                } else {
                  this.onClientClear();
                }
              }}
            />
            <FieldError id={`${CLIENT_ID}-error`}>{errors.client}</FieldError>
            {this.selectedClient && this.selectedClient.attributes.do_not_service &&
            <div className="alert mt-2 alert-danger do-not-service" role="alert">
              Do not service this client: {this.selectedClient.attributes.do_not_service_reason}
            </div>}
            {this.selectedClient && this.selectedClient.attributes.is_on_alert &&
            <div className="alert mt-2 alert-danger do-not-service" role="alert">
              Alert on this client: {this.selectedClient.attributes.on_alert_reason}
            </div>}
          </FormGroup>
          {this.selectedClient && (
            <FormGroup
              label="Location"
              labelFor={LOCATION_ID}
              labelInfo={<span className="fb-required" aria-hidden="true">*</span>}
              className={errors.location ? "has-error" : undefined}
            >
              <LocationAutocomplete
                key={locationScope || "none"}
                scope={{ client_id: locationScope }}
                inputProps={{
                  id: LOCATION_ID,
                  fill: true,
                  placeholder: "Start typing one of this client's addresses…",
                  "aria-required": true,
                  "aria-invalid": errors.location ? true : undefined,
                  "aria-describedby": errors.location ? `${LOCATION_ID}-error` : undefined
                }}
                onLocationClear={this.onLocationClear}
                value={this.selectedLocation}
                onLocationSelect={location => {
                  if (location) {
                    this.selectedLocation = location;
                    this.errors = {};
                  } else {
                    this.onLocationClear();
                  }
                }}
              />
              <FieldError id={`${LOCATION_ID}-error`}>{errors.location}</FieldError>
            </FormGroup>
          )}
        </FormSection>
      </FormDrawer>
    );
  }
}

export default UpdateClientModal;
